/**
 * Clause tokenization (plan §3).
 *
 * @remarks
 * A folded, separator-free clause is single-spaced by construction (the
 * folding stage collapsed whitespace and the splitter trimmed), so
 * tokenization is a positional walk: split on single spaces, recording each
 * token's half-open span into the clause text. Spans are what let corrections
 * and diagnostics point at exactly the word they mean ("(opening the *gate*)").
 *
 * Stopword/article filtering is deliberately NOT done here — it needs the
 * compiled lexicon (built-ins + caller extensions), so it happens in the
 * canonicalization stage where the lexicon is in hand. Tokenize stays pure
 * string mechanics.
 *
 * @packageDocumentation
 */

import { type Token } from '../types.js';

/**
 * Tokenize one folded clause into position-preserving tokens.
 *
 * @param clauseText - A single clause as produced by the splitters (folded, trimmed, single-spaced)
 * @returns Tokens in order; empty for an empty clause
 */
export const tokenizeClause = (clauseText: string): readonly Token[] => {
	if (clauseText === '') {
		return [];
	}

	const tokens: Token[] = [];

	let cursorPosition = 0;

	for (const tokenText of clauseText.split(' ')) {
		tokens.push({
			sourceSpan: {
				end: cursorPosition + tokenText.length,
				start: cursorPosition,
			},
			text: tokenText,
		});
		cursorPosition += tokenText.length + 1;
	}

	return tokens;
};
